import React, { useState, useMemo, useEffect, useContext } from 'react'
import DateFnsUtils from '@date-io/date-fns'
import { yupResolver } from '@hookform/resolvers'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import { VoiceTextField } from '@ifca-root/react-component/src/components/Input/CustomTextField'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import { TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import { ExitConfirmationDialog } from 'components/Dialog/ExitConfirmationDialog'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { AccountFooter } from 'components/Footer/AccountFooter'
import { Controller, useForm } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import SnackBarContext from 'containers/App/Store/SnackBarContext'
import { SystemMsgs } from 'helpers/Messages/SystemMsg'
import {
  useGetAssetRegistrationQuery,
  useGetCurrentReversalInfoLazyQuery,
  useCreateFaProvisionImpairmentReversalMutation,
  useUpdateFaProvisionImpairmentReversalMutation,
  useGetDocumentDateValidationQuery,
  useLatestOpenPeriodCheckingDateQuery,
  ApprovalStatus,
} from 'generated/graphql'
import * as yup from 'yup'

interface CurrentProvisionImpairmentReversalInfo {
  baseOriginalAmount: number
  currentOriginalValue: number
  currentAccumulatedDepreciation: number
  currentAccumulatedImpairment: number
  currentAccumulatedReversal: number
  currentNetBookValue: number
  residualValue: number
  maxAllowedReversal: number
  quantity: number
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

export const ProvisionImpairmentReversalForm = (props: any) => {
  const history = useHistory()
  const location = useLocation()
  const editData: any = location?.state
  const { formMode } = props
  const { CompanyID }: any = useParams()
  const user = JSON.parse(localStorage.getItem('loggedInUser'))

  /* -------------------------------------------- */
  /*                     STATE                    */
  /* -------------------------------------------- */
  const [errorDia, setErrorDia] = useState<boolean>(false)
  const [errMsg, setErrMsg] = useState<string>('')
  const [openExitConf, setOpenExitConf] = useState(false)
  const [record, setRecord] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<any>(
    editData?.Asset || null
  )
  const [
    currentInfo,
    setCurrentInfo,
  ] = useState<CurrentProvisionImpairmentReversalInfo | null>(null)
  const { setOpenSnackBar, setSnackBarMsg }: any = useContext(SnackBarContext)

  let mode = formMode
  if (formMode === 'resubmit') {
    mode = 'edit'
  }

  /* -------------------------------------------- */
  /*                   USE FORM                   */
  /* -------------------------------------------- */
  const provisionImpairmentReversalSchema = yup.object().shape({
    asset_no: yup.mixed().required('Asset is required'),
    reversal_date: yup.date().required('Reversal Date is required'),
    reversal_amount: yup
      .number()
      .transform((value, originalValue) => {
        if (
          originalValue === '' ||
          originalValue === null ||
          originalValue === undefined
        )
          return null
        return typeof originalValue === 'string'
          ? parseFloat(originalValue.replace(/,/g, ''))
          : originalValue
      })
      .nullable()
      .required('Reversal Amount is required')
      .moreThan(0, 'Reversal Amount must be greater than 0'),
    description: yup.string().required('Description is required'),
    reason: yup.string().required('Reason is required'),
  })

  const { setValue, control, handleSubmit, watch, errors } = useForm<any>({
    defaultValues: {
      reversal_date: editData?.reversal_date || new Date(),
      reversal_amount: editData?.reversal_amount || null,
      description: editData?.description || '',
      reason: editData?.reason || '',
    },
    mode: 'onSubmit',
    resolver: yupResolver(provisionImpairmentReversalSchema),
  })

  const watchedReversalAmount = watch('reversal_amount')

  const calculatedNewAccumulatedImpairmentReversal = useMemo(() => {
    const reversal = parseFloat(watchedReversalAmount) || 0
    const existingReversal = currentInfo?.currentAccumulatedReversal || 0
    return existingReversal + reversal
  }, [watchedReversalAmount, currentInfo])

  const calculatedRemainingImpairment = useMemo(() => {
    const reversal = parseFloat(watchedReversalAmount) || 0
    const accumulated = currentInfo?.currentAccumulatedImpairment || 0
    const existingReversal = currentInfo?.currentAccumulatedReversal || 0
    return Math.max(0, accumulated - existingReversal - reversal)
  }, [watchedReversalAmount, currentInfo])

  const availableForReversal = useMemo(() => {
    const accumulated = currentInfo?.currentAccumulatedImpairment || 0
    const existingReversal = currentInfo?.currentAccumulatedReversal || 0
    return Math.max(0, accumulated - existingReversal)
  }, [currentInfo])

  /* -------------------------------------------- */
  /*                    QUERY                     */
  /* -------------------------------------------- */
  const {
    loading: assetsLoading,
    data: { getAssetRegistration } = { getAssetRegistration: [] },
  } = useGetAssetRegistrationQuery({
    fetchPolicy: 'network-only',
    variables: {
      CompanyID: CompanyID,
    },
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const [
    getCurrentReversalInfo,
    { loading: currentInfoLoading },
  ] = useGetCurrentReversalInfoLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: data => {
      if (data?.getCurrentReversalInfo) {
        const info = data.getCurrentReversalInfo as CurrentProvisionImpairmentReversalInfo
        setCurrentInfo(info)
        setValue('original_amount', info.currentOriginalValue)
        setValue('quantity', info.quantity)
        setValue('residual_value', info.residualValue)
        setValue(
          'accumulated_depreciated_amount',
          info.currentAccumulatedDepreciation
        )
        setValue('accumulated_impairment', info.currentAccumulatedImpairment)
        setValue(
          'existing_impairment_reversal',
          info.currentAccumulatedReversal
        )
      }
    },
  })

  const {
    loading: openPeriodCheckDateLoading,
    data: { latestOpenPeriodCheckingDate } = {
      latestOpenPeriodCheckingDate: {} as any,
    },
  } = useLatestOpenPeriodCheckingDateQuery({
    fetchPolicy: 'network-only',
    variables: { CompanyID },
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const {
    loading: docDateValidationLoading,
    data: { getDocumentDateValidation } = { getDocumentDateValidation: [] },
  } = useGetDocumentDateValidationQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const assetsList = useMemo(() => {
    return getAssetRegistration || []
  }, [getAssetRegistration])

  useEffect(() => {
    if (
      (formMode === 'edit' || formMode === 'resubmit') &&
      editData?.fa_asset_id &&
      assetsList.length > 0
    ) {
      const asset = assetsList.find(a => a.fa_asset_id === editData.fa_asset_id)
      if (asset) {
        setSelectedAsset(asset)
        setValue('asset_name', asset.name || '')
        setValue('start_date', asset.start_use_date)
        setValue('model_no', asset.model_no || '')
        getCurrentReversalInfo({
          variables: {
            fa_asset_id: asset.fa_asset_id,
            exclude_reversal_id:
              editData.provision_impairment_reversal_id || null,
          },
        })
      }
    }
  }, [formMode, editData, assetsList])

  /* -------------------------------------------- */
  /*                   MUTATION                   */
  /* -------------------------------------------- */
  const [
    createReversal,
    { loading: createLoading },
  ] = useCreateFaProvisionImpairmentReversalMutation({
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.createNewRecord())
      history.push(
        `/fixed-assets/${CompanyID}/assets-change/provision-for-impairment-reversal`
      )
    },
  })

  const [
    updateReversal,
    { loading: updateLoading },
  ] = useUpdateFaProvisionImpairmentReversalMutation({
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.updateRecord())
      history.push(
        `/fixed-assets/${CompanyID}/assets-change/provision-for-impairment-reversal`
      )
    },
  })

  /* -------------------------------------------- */
  /*                   HANDLERS                   */
  /* -------------------------------------------- */
  const onSubmit = (data: any, approvalStatus: ApprovalStatus) => {
    if (approvalStatus === ApprovalStatus.Submit) {
      if (isPeriodClosed) {
        setErrMsg(
          'Cannot submit. The accounting period is closed or does not exist. ' +
            'Please select a date within the open financial period ' +
            (latestOpenPeriodCheckingDate?.StartDate &&
            latestOpenPeriodCheckingDate?.EndDate
              ? `(${latestOpenPeriodCheckingDate.StartDate} to ${latestOpenPeriodCheckingDate.EndDate}).`
              : '.')
        )
        setErrorDia(true)
        return
      }

      if (isDocDateInvalid) {
        setErrMsg(
          'Cannot submit. The reversal date is outside the allowed validation range. ' +
            'Please select a date within the valid period.'
        )
        setErrorDia(true)
        return
      }
    }

    const currentAsset = selectedAsset || editData?.Asset

    if (!currentAsset) {
      setErrMsg('Please select an asset')
      setErrorDia(true)
      return
    }

    const reversalAmount = parseFloat(data.reversal_amount) || 0

    if (reversalAmount <= 0) {
      setErrMsg('Reversal amount must be greater than 0')
      setErrorDia(true)
      return
    }

    if (reversalAmount > availableForReversal) {
      setErrMsg(
        `Reversal amount (${formatCurrency(
          reversalAmount
        )}) cannot exceed available impairment for reversal (${formatCurrency(
          availableForReversal
        )}).`
      )
      setErrorDia(true)
      return
    }

    const input = {
      provision_impairment_reversal_id:
        editData?.provision_impairment_reversal_id || undefined,
      fa_asset_id: selectedAsset?.fa_asset_id || editData?.fa_asset_id,
      company_id: CompanyID,
      reversal_date: data.reversal_date,
      description: data.description,
      reason: data.reason,
      residual_value: currentInfo?.residualValue || 0,
      accumulated_depreciated_amount:
        currentInfo?.currentAccumulatedDepreciation || 0,
      accumulated_impairment: currentInfo?.currentAccumulatedImpairment || 0,
      reversal_amount: reversalAmount,
      accumulated_impairment_reversal: calculatedNewAccumulatedImpairmentReversal,
      ApprovalStatus: approvalStatus,
    }

    if (formMode === 'add') {
      createReversal({ variables: { input } })
    } else {
      updateReversal({ variables: { input } })
    }
  }

  const handleAssetChange = (value: any) => {
    if (value) {
      setSelectedAsset(value)
      setValue('asset_no', value)
      setValue('asset_name', value?.name)
      setValue('start_date', value?.start_use_date)
      setValue('model_no', value?.model_no)
      getCurrentReversalInfo({
        variables: {
          fa_asset_id: value.fa_asset_id,
          exclude_reversal_id:
            editData?.provision_impairment_reversal_id || null,
        },
      })
    } else {
      setSelectedAsset(null)
      setValue('asset_no', null)
      setCurrentInfo(null)
      setValue('original_amount', 0)
      setValue('quantity', 0)
      setValue('residual_value', 0)
      setValue('accumulated_depreciated_amount', 0)
      setValue('accumulated_impairment', 0)
      setValue('existing_impairment_reversal', 0)
    }
  }

  /* -------------------------------------------- */
  /*               PERIOD VALIDATION              */
  /* -------------------------------------------- */
  const watchReversalDate =
    watch('reversal_date') || editData?.reversal_date || new Date()
  const reversalDateStr = (() => {
    const d = new Date(watchReversalDate)
    const year = d.getFullYear()
    const month = (d.getMonth() + 1).toString().padStart(2, '0')
    const day = d
      .getDate()
      .toString()
      .padStart(2, '0')
    return `${year}-${month}-${day}`
  })()

  const isPeriodClosed =
    latestOpenPeriodCheckingDate?.StartDate &&
    latestOpenPeriodCheckingDate?.EndDate
      ? reversalDateStr < latestOpenPeriodCheckingDate?.StartDate ||
        reversalDateStr > latestOpenPeriodCheckingDate?.EndDate
      : false

  const today = new Date()
  const monthsBefore = getDocumentDateValidation?.[0]?.MonthsBefore ?? 12
  const monthsAfter = getDocumentDateValidation?.[0]?.MonthsAfter ?? 1

  const minValidDate = new Date(today)
  minValidDate.setMonth(minValidDate.getMonth() - monthsBefore)
  minValidDate.setHours(0, 0, 0, 0)

  const maxValidDate = new Date(today)
  maxValidDate.setMonth(maxValidDate.getMonth() + monthsAfter)
  maxValidDate.setHours(23, 59, 59, 999)

  const reversalDate = new Date(watchReversalDate)
  const isDocDateInvalid =
    reversalDate.getTime() < minValidDate.getTime() ||
    reversalDate.getTime() > maxValidDate.getTime()

  /* -------------------------------------------- */
  /*                    FOOTER                    */
  /* -------------------------------------------- */
  const draftFooterOption = {
    name: 'Save as Draft',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Active)),
    color: 'primary',
    props: { type: 'button' },
  }

  const submitFooterOption = {
    name: 'Submit',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Submit)),
    color: 'primary',
    props: { type: 'button' },
  }

  let footerOptions: any[]
  if (formMode === 'add' || formMode === 'edit' || formMode === 'resubmit') {
    footerOptions = [draftFooterOption, submitFooterOption]
  } else {
    footerOptions = []
  }

  return (
    <>
      {(assetsLoading ||
        createLoading ||
        updateLoading ||
        currentInfoLoading) && <Loading />}

      <MainHeader
        mainBtn="close"
        onClick={() => {
          history.push(
            `/fixed-assets/${CompanyID}/assets-change/provision-for-impairment-reversal`
          )
        }}
        smTitle={'Fixed Assets'}
        title={user?.companyName}
        routeSegments={[
          { name: 'Fixed Assets' },
          { name: 'Assets Change' },
          { name: 'Provision for Impairment Reversal', current: true },
        ]}
        rightRouteSegments={[
          {
            name:
              formMode === 'add'
                ? 'New'
                : formMode === 'resubmit'
                ? 'Resubmit'
                : 'Edit',
            current: true,
          },
        ]}
      />

      <ContentWrapper float footer>
        <CardContents
          section={{
            header: {
              title: 'Assets Information',
            },
          }}
        >
          <Controller
            render={({ value, onChange }) => {
              const defVal = editData?.Asset || null

              return (
                <Autocomplete
                  options={
                    assetsList?.sort((a, b) => {
                      return a?.DocNo?.localeCompare(b?.DocNo)
                    }) || []
                  }
                  getOptionLabel={option =>
                    `${option?.DocNo} - ${option?.name}`
                  }
                  fullWidth
                  onChange={(event, value: any) => handleAssetChange(value)}
                  defaultValue={defVal}
                  disabled={mode === 'approve-reject'}
                  renderOption={props => {
                    return (
                      <div>
                        <div>
                          <span className="xsTitle">{props?.DocNo}</span>
                          <span className="desc" style={{ marginLeft: '8px' }}>
                            {props?.name}
                          </span>
                        </div>
                      </div>
                    )
                  }}
                  renderInput={(params: any) => {
                    return (
                      <TextField
                        {...params}
                        label="Asset"
                        style={{ width: '100%' }}
                        required
                        margin="normal"
                        helperText={errors?.asset_no?.message}
                        error={errors?.asset_no ? true : false}
                      />
                    )
                  }}
                />
              )
            }}
            name="asset_no"
            control={control}
            margin="normal"
            defaultValue={editData?.Asset || null}
          />

          <Controller
            as={TextField}
            name="asset_name"
            label="Asset Name"
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            defaultValue={editData?.Asset?.name || 'N/A'}
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
          />

          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              render={({ value, onChange }) => (
                <KeyboardDatePicker
                  value={value}
                  onChange={onChange}
                  label="Start Date"
                  format="dd/MM/yyyy"
                  readOnly
                  margin="dense"
                  allowKeyboardControl
                  KeyboardButtonProps={{
                    style: { display: 'none' },
                  }}
                  className="left"
                  InputProps={{
                    disableUnderline: true,
                    readOnly: true,
                  }}
                />
              )}
              name="start_date"
              control={control}
              defaultValue={editData?.Asset?.start_use_date || new Date()}
            />
          </MuiPickersUtilsProvider>

          <Controller
            as={TextField}
            name="model_no"
            label="Model No"
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            className="right"
            defaultValue={editData?.Asset?.model_no || 'N/A'}
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={currentInfo?.currentOriginalValue ?? value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Original Amount"
                fullWidth
                margin="dense"
                className="left"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="original_amount"
            control={control}
            defaultValue={editData?.Asset?.base_original_amount || 0}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={currentInfo?.quantity ?? value}
                thousandSeparator
                decimalScale={0}
                fixedDecimalScale
                label="Quantity"
                fullWidth
                margin="dense"
                className="right"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="quantity"
            control={control}
            defaultValue={editData?.Asset?.quantity || 0}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={currentInfo?.residualValue ?? value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Residual Value"
                fullWidth
                margin="dense"
                className="left"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="residual_value"
            control={control}
            defaultValue={editData?.residual_value || 0}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={currentInfo?.currentAccumulatedDepreciation ?? value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Acc. Depreciated Amt"
                fullWidth
                margin="dense"
                className="right"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="accumulated_depreciated_amount"
            control={control}
            defaultValue={editData?.accumulated_depreciated_amount || 0}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={currentInfo?.currentAccumulatedImpairment ?? value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Accumulated Impairment"
                fullWidth
                margin="dense"
                className="left"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="accumulated_impairment"
            control={control}
            defaultValue={editData?.accumulated_impairment || 0}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={currentInfo?.currentAccumulatedReversal ?? value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Existing Impairment Reversal"
                fullWidth
                margin="dense"
                className="right"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="existing_impairment_reversal"
            control={control}
            defaultValue={0}
          />

          <NumberFormat
            customInput={TextField as any}
            value={availableForReversal}
            thousandSeparator
            decimalScale={2}
            fixedDecimalScale
            label="Available for Reversal"
            fullWidth
            margin="dense"
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
            helperText="Accumulated Impairment - Existing Reversals"
          />
        </CardContents>

        <CardContents
          section={{
            header: {
              title: 'Impairment Reversal',
            },
          }}
        >
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              render={({ value, onChange }) => (
                <KeyboardDatePicker
                  value={value}
                  onChange={onChange}
                  label="Date"
                  format="dd/MM/yyyy"
                  margin="dense"
                  allowKeyboardControl
                  KeyboardButtonProps={{
                    'aria-label': 'date',
                  }}
                  className="left"
                  required
                  disabled={mode === 'approve-reject'}
                />
              )}
              name="reversal_date"
              control={control}
              defaultValue={editData?.reversal_date || new Date()}
            />
          </MuiPickersUtilsProvider>

          <Controller
            render={({ value, onChange }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                onValueChange={({ floatValue }) => onChange(floatValue)}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Reversal Amount"
                fullWidth
                margin="dense"
                className="right"
                required
                disabled={mode === 'approve-reject'}
                helperText={
                  errors?.reversal_amount?.message ||
                  `Max: ${formatCurrency(availableForReversal)}`
                }
                error={errors?.reversal_amount ? true : false}
              />
            )}
            name="reversal_amount"
            control={control}
            defaultValue={editData?.reversal_amount || ''}
          />

          <NumberFormat
            customInput={TextField as any}
            value={calculatedNewAccumulatedImpairmentReversal}
            thousandSeparator
            decimalScale={2}
            fixedDecimalScale
            label="New Accumulated Impairment Reversal"
            fullWidth
            margin="dense"
            className="left"
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
          />

          <NumberFormat
            customInput={TextField as any}
            value={calculatedRemainingImpairment}
            thousandSeparator
            decimalScale={2}
            fixedDecimalScale
            label="Remaining Impairment"
            fullWidth
            margin="dense"
            className="right"
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
          />

          <VoiceTextField
            mounted={true}
            label="Description"
            name="description"
            setValue={setValue}
            record={record}
            setRecord={setRecord}
            control={control}
            controllerProps={{
              autoComplete: 'off',
              defaultValue: editData?.description || '',
              helperText: errors?.description?.message,
              error: errors?.description ? true : false,
            }}
            disabled={mode === 'approve-reject'}
            required
          />

          <VoiceTextField
            mounted={true}
            label="Reason"
            name="reason"
            setValue={setValue}
            record={record}
            setRecord={setRecord}
            control={control}
            controllerProps={{
              autoComplete: 'off',
              defaultValue: editData?.reason || '',
              helperText: errors?.reason?.message,
              error: errors?.reason ? true : false,
            }}
            disabled={mode === 'approve-reject'}
            required
          />
        </CardContents>
      </ContentWrapper>

      {footerOptions?.length > 0 && (
        <AccountFooter options={[...footerOptions]} />
      )}

      <ErrorDialog
        errorDia={errorDia}
        setErrorDia={setErrorDia}
        errorMsg={errMsg}
        errorHeaderMsg={'Error'}
      />

      <ExitConfirmationDialog
        openExitConf={openExitConf}
        setOpenExitConf={setOpenExitConf}
        onConfirm={() => {
          history.push(
            `/fixed-assets/${CompanyID}/assets-change/provision-for-impairment-reversal`
          )
        }}
      />
    </>
  )
}
